import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { COMBAT, RENDER, SPAWN, WARP } from '../config';
import { Loop } from '../core/Loop';
import { Input, type FlightIntent } from '../core/Input';
import { createRenderStack, type RenderStack } from '../core/Renderer';
import { Universe } from '../astro/Universe';
import { SolarSystemView } from '../world/SolarSystemView';
import { PlayerShip, type ShipEvent } from '../ship/PlayerShip';
import { ShipCamera } from '../ship/ShipCamera';
import { ShipVisual } from '../ship/ShipVisual';
import { SHIP_BY_ID, SHIPS, type ShipColor } from '../ship/ShipCatalog';
import { Hud } from '../ui/Hud';
import { Markers, type MarkerInput } from '../ui/Markers';
import { SystemMap, type MapPoint } from '../ui/SystemMap';
import { Autopilot, type TargetInfo, type TargetRef } from '../ship/Autopilot';
import { SpeedDust } from '../world/SpeedDust';
import { EarthTiles } from '../world/EarthTiles';
import { Asteroids, ASTEROIDS, earthSunBasis, spawnLocal, type Rock } from '../world/Asteroids';
import { AsteroidBelt } from '../world/AsteroidBelt';
import { Effects } from '../world/Effects';
import { WarpTunnel } from '../world/WarpTunnel';
import { CockpitInterior, type CockpitReadout } from '../ship/CockpitInterior';
import { formatDistance, formatSpeed } from '../ui/format';
import { Audio } from '../audio/Audio';
import { Logbook } from './Logbook';
import { PauseMenu } from '../ui/PauseMenu';
import { Multiplayer } from './Multiplayer';

export type GameOptions = { name: string; shipId: string; color: ShipColor };

export class Game {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(RENDER.fovChase, 1, RENDER.near, RENDER.far);
  readonly stack: RenderStack;
  readonly universe = new Universe();
  readonly view: SolarSystemView;
  readonly input: Input;
  ship: PlayerShip;
  shipVisual: ShipVisual;
  readonly shipCamera = new ShipCamera(this.camera);
  readonly hud = new Hud();
  readonly markers = new Markers(document.getElementById('app')!);
  readonly map: SystemMap;
  readonly autopilot = new Autopilot();
  readonly dust = new SpeedDust();
  readonly earthTiles: EarthTiles;
  readonly asteroids: Asteroids;
  readonly belt = new AsteroidBelt();
  readonly effects = new Effects();
  readonly warpTunnel = new WarpTunnel();
  readonly cockpit = new CockpitInterior();
  score = 0;
  readonly audio = new Audio();
  readonly logbook = new Logbook();
  readonly pause: PauseMenu;
  mode: 'menu' | 'play' = 'menu';
  private menuAngle = 2.2;
  private menuFrozen = false;
  private menuDistance = 1.15;
  private logCheckTimer = 0;
  onChangeShipRequest: () => void = () => {};
  mp: Multiplayer | null = null;
  private joined = false;
  private pausedByUnlockAt = -Infinity;
  private trauma = 0;
  private shakeT = 0;
  private damageFlash = 0;
  private respawnTimer: number | null = null;
  private fireCooldown = 0;
  private muzzleSide = 1;
  shotsFired = 0;
  /** network hooks (M6) */
  onRockDestroyed: (id: string) => void = () => {};
  onFire: (from: THREE.Vector3, dir: THREE.Vector3) => void = () => {};
  onRespawn: () => void = () => {};
  target: TargetRef | null = { kind: 'body', id: 'moon' };
  private readonly localVelocity = new THREE.Vector3();
  private readonly occluders: { position: THREE.Vector3; radius: number }[];
  private readonly loop = new Loop((dt) => this.update(dt), () => this.render());
  private readonly intent: FlightIntent = {
    pitch: 0, yaw: 0, roll: 0, throttleDelta: 0, strafeUp: 0, boost: false, fire: false, fullStop: false,
  };
  private readonly events: ShipEvent[] = [];
  /** offset between server and local clocks (ms) */
  clockOffset = 0;
  frame = 0;
  private lastEventText = '';
  private lastEventTime = 0;
  private get lastEvent(): string {
    return performance.now() - this.lastEventTime < 4000 ? this.lastEventText : '';
  }
  private set lastEvent(v: string) {
    this.lastEventText = v;
    this.lastEventTime = performance.now();
  }

  constructor(canvas: HTMLCanvasElement, readonly options: GameOptions) {
    this.stack = createRenderStack(canvas, this.scene, this.camera);
    this.input = new Input(canvas);
    this.view = new SolarSystemView(this.stack.renderer, this.universe);
    this.scene.add(this.view.group);
    this.scene.add(this.camera);

    const pmrem = new THREE.PMREMGenerator(this.stack.renderer);
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    this.scene.environmentIntensity = 0.35;

    const spec = SHIP_BY_ID.get(options.shipId) ?? SHIPS[0];
    this.ship = new PlayerShip(spec);
    this.shipVisual = new ShipVisual(spec, options.color);
    this.scene.add(this.shipVisual.group);
    this.shipCamera.cockpitEye.copy(this.shipVisual.model.cockpitEye);
    this.camera.add(this.warpTunnel.mesh);
    this.camera.add(this.cockpit.group);
    this.cockpit.setVisible(false);

 this.earthTiles = new EarthTiles(this.universe.get('earth'), this.stack.renderer);
    this.scene.add(this.earthTiles.group);
    this.occluders = this.universe.bodies.map((b) => ({ position: b.position, radius: b.def.radius }));
    this.scene.add(this.dust.object);
    this.asteroids = new Asteroids(this.universe);
    this.scene.add(this.asteroids.group, this.belt.points, this.effects.group);
    this.map = new SystemMap(document.getElementById('app')!, this.universe);
    this.map.onSelect = (key) => {
      const [kind, id] = key.split(':') as ['body' | 'player', string];
      this.setTarget({ kind, id });
    };

    this.score = this.logbook.points;
    this.logbook.onNew = (e) => {
      this.hud.toast(`LOGBOOK · ${e.label}`, `${this.logbook.count}/${this.logbook.total}`);
      this.audio.ui('log');
    };
    this.pause = new PauseMenu(document.getElementById('app')!, this.audio.volume, this.audio.isMuted);
    this.pause.onResume = () => this.setPaused(false);
    this.pause.onChangeShip = () => {
      this.setPaused(false);
      this.onChangeShipRequest();
    };
    this.pause.onVolume = (v) => this.audio.setVolume(v);
    this.pause.onMute = () => this.audio.toggleMute();

    // Browsers swallow the first Esc to release pointer lock: treat an unexpected unlock as "pause".
    document.addEventListener('pointerlockchange', () => {
      if (document.pointerLockElement) return;
      if (this.mode === 'play' && !this.pause.open && !this.map.open && !this.mp?.social.chatOpen && !this.ship.dead) {
        this.pausedByUnlockAt = performance.now();
        this.setPaused(true);
      }
    });

    this.universe.update(this.now());
    this.spawn();
    this.setMode('menu');
    this.installTestHooks();
  }

  now(): number {
    return Date.now() + this.clockOffset;
  }

  async load(onProgress?: (done: number, total: number) => void): Promise<void> {
    await Promise.all([this.view.preload(onProgress), this.shipVisual.ready, this.asteroids.load()]);
    const debris: { geometry: THREE.BufferGeometry; material: THREE.Material }[] = [];
    this.asteroids.group.traverse((o) => {
      const m = o as THREE.InstancedMesh;
      if (m.isInstancedMesh && debris.length < 3) debris.push({ geometry: m.geometry, material: m.material as THREE.Material });
    });
    this.effects.setDebrisSources(debris);
  }

  start(): void {
    this.loop.start();
  }

  /** Shared spawn: 400 km over the day side, facing prograde towards The Drift. */
  spawn(): void {
    const earth = this.universe.get(SPAWN.bodyId);
    const basis = earthSunBasis(this.universe, new THREE.Matrix4());
    const local = spawnLocal(new THREE.Vector3(), earth.def.radius, SPAWN.altitude);
    // Small random offset so players spawning together don't overlap.
    local.add(new THREE.Vector3((Math.random() - 0.5) * 5000, (Math.random() - 0.5) * 5000, (Math.random() - 0.5) * 1600));
    const pos = local.clone().applyMatrix4(basis).add(earth.position);
    const up = local.clone().normalize().applyMatrix4(new THREE.Matrix4().extractRotation(basis));
    const forward = new THREE.Vector3(0, 0, 1).applyMatrix4(new THREE.Matrix4().extractRotation(basis)).cross(up).normalize();
    this.ship.placeWorld(pos, up, forward);
    this.ship.resetShield();
    this.shipCamera.snap(this.ship);
  }

  // ---------------- Modes ----------------
  setMode(mode: 'menu' | 'play'): void {
    this.mode = mode;
    const play = mode === 'play';
    this.mp?.social.setVisible(play);
    this.hud.setVisible(play);
    this.markers.setVisible(play);
    this.input.enabled = play;
    if (!play) {
      if (this.map.open) this.map.toggle();
      this.shipCamera.mode = 'chase';
    }
  }

  setPaused(p: boolean): void {
    if (p) this.pause.show(this.logbook);
    else {
      this.pause.hide();
      this.input.requestPointerLock();
    }
  }

  /** Swap the hull (menu preview or respawn with a new ship). */
  async setShip(shipId: string, color: ShipColor): Promise<void> {
    const spec = SHIP_BY_ID.get(shipId) ?? SHIPS[0];
    if (spec.id === this.shipVisual.spec.id && color === this.options.color) return;
    this.options.shipId = spec.id;
    this.options.color = color;
    const visual = new ShipVisual(spec, color);
    await visual.ready;
    if (this.options.shipId !== spec.id || this.options.color !== color) return; // superseded
    this.scene.remove(this.shipVisual.group);
    this.shipVisual = visual;
    this.scene.add(visual.group);
    this.ship.spec = spec;
    this.ship.resetShield();
    this.shipCamera.cockpitEye.copy(visual.model.cockpitEye);
  }

  attachMultiplayer(url: string): Multiplayer {
    this.mp = new Multiplayer(this, url);
    return this.mp;
  }

  launch(name: string): void {
    this.options.name = name;
    if (this.mp) {
      if (!this.joined) this.mp.join(name, this.ship.spec.id, this.options.color);
      else this.mp.changeShip(this.ship.spec.id, this.options.color);
      this.joined = true;
    }
    this.audio.unlock();
    this.spawn();
    this.setMode('play');
    this.input.requestPointerLock();
    this.hud.toast(`Welcome aboard, ${name}`, this.logbook.next() ? `Next: ${this.logbook.next()!.label}` : 'Grand Tour complete');
  }

  private updateMenuCamera(dt: number): void {
    // Slow orbit around the parked ship with Earth and The Drift behind it.
    if (!this.menuFrozen) this.menuAngle += dt * 0.12;
    const L = this.ship.spec.length;
    const q = this.ship.quaternion;
    const r = L * this.menuDistance;
    const offset = new THREE.Vector3(Math.sin(this.menuAngle) * r, L * 0.28, Math.cos(this.menuAngle) * r - L * 0.1);
    const world = offset.clone().applyQuaternion(q);
    this.shipCamera.worldPosition.copy(this.ship.position).add(world);
    const look = new THREE.Matrix4().lookAt(world, new THREE.Vector3(0, -L * 0.1, 0).applyQuaternion(q), this.ship.up());
    this.camera.position.set(0, 0, 0);
    this.camera.quaternion.setFromRotationMatrix(look);
    this.camera.updateMatrixWorld(true);
  }

  // ---------------- Combat ----------------
  private addTrauma(v: number): void {
    this.trauma = Math.min(1, this.trauma + v);
  }

  private damage(amount: number): void {
    if (this.ship.dead) return;
    this.ship.shield -= amount;
    this.ship.lastDamageTime = performance.now() / 1000;
    this.damageFlash = Math.min(1, this.damageFlash + amount / 40);
    this.addTrauma(Math.min(0.8, 0.25 + amount / 80));
    this.audio.shieldHit();
    if (this.ship.shield <= 0) this.kill('Shield depleted');
  }

  private kill(reason: string): void {
    if (this.ship.dead) return;
    this.ship.dead = true;
    this.ship.shield = 0;
    this.autopilot.disengage(this.ship);
    const anchor = this.ship.frameBody ?? this.universe.get('earth');
    this.effects.explosion(anchor, this.ship.position, this.ship.spec.length);
    this.addTrauma(1);
    this.respawnTimer = COMBAT.respawnDelay;
    this.audio.explosion();
    this.lastEvent = reason;
    this.ship.velocity.set(0, 0, 0);
    this.ship.throttle = 0;
  }

  private updateCombat(dt: number): void {
    const ship = this.ship;
    if (ship.dead) return;
    // Shield regeneration
    if (performance.now() / 1000 - ship.lastDamageTime > COMBAT.regenDelay) ship.shield = Math.min(ship.maxShield, ship.shield + COMBAT.shieldRegen * dt);

    // Rock collisions: capsule along the hull.
    const fwd = ship.forward();
    const half = ship.spec.length * 0.45;
    const hullR = ship.spec.length * ship.spec.frigate.width * 0.45;
    const a = ship.position.clone().addScaledVector(fwd, half);
    const b = ship.position.clone().addScaledVector(fwd, -half);
    const hitInfo = this.asteroids.overlappingCapsule(a, b, hullR);
    if (hitInfo) {
      const hit = hitInfo.rock;
      const away = hitInfo.closest.clone().sub(hit.world).normalize();
      const impact = Math.max(0, -ship.velocity.dot(away));
      const push = hit.radius * 0.8 + hullR - hitInfo.closest.distanceTo(hit.world);
      ship.position.addScaledVector(away, push + 0.5);
      ship.velocity.addScaledVector(away, impact * 1.2 + 8);
      if (impact > 4) {
        this.damage(COMBAT.rockHitDamage * Math.min(3, Math.max(0.3, impact / 60)));
        const at = hitInfo.closest.clone().addScaledVector(away, -hullR);
        this.effects.burst(this.asteroids.frameOf(hit), at, 40, new THREE.Color(0.6, 0.85, 1), 6, 60, 0.6);
        this.effects.burst(this.asteroids.frameOf(hit), at, 25, new THREE.Color(1, 0.6, 0.3), 3, 90, 0.4);
      }
    }

    // Lasers
    this.fireCooldown -= dt;
    if (this.intent.fire && this.fireCooldown <= 0 && !ship.landed) {
      this.fireCooldown = COMBAT.laserCooldown;
      this.fireLaser();
    }
  }

  private fireLaser(): void {
    this.shotsFired++;
    const ship = this.ship;
    const fwd = ship.forward();
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(ship.quaternion);
    this.muzzleSide *= -1;
    const up = ship.up();
    const from = ship.position.clone().addScaledVector(fwd, ship.spec.length * 0.42).addScaledVector(right, this.muzzleSide * ship.spec.length * 0.05).addScaledVector(up, ship.spec.length * 0.03);
    // Aim assist towards a rock near the crosshair.
    const assist = this.asteroids.aimAssist(from, fwd, COMBAT.laserRange, (COMBAT.aimAssistDeg * Math.PI) / 180);
    const dir = assist ? assist.world.clone().sub(from).normalize() : fwd;
    const result = this.asteroids.raycast(from, dir, COMBAT.laserRange);
    const color = new THREE.Color(ship.spec.engineColor);
    const anchor = ship.frameBody ?? this.universe.get('earth');
    this.effects.laser(anchor, from, dir, color, result ? result.distance : COMBAT.laserRange);
    this.onFire(from, dir);
    this.audio.laser();
    if (result) this.hitRock(result.rock, from.clone().addScaledVector(dir, result.distance), true);
  }

  /** Apply a laser hit (local or from the network). */
  hitRock(rock: Rock, at: THREE.Vector3, local: boolean): void {
    if (this.asteroids.isDestroyed(rock.id, this.now())) return;
    rock.hp -= 1;
    const anchor = this.asteroids.frameOf(rock);
    this.effects.burst(anchor, at, 14, new THREE.Color(1, 0.7, 0.35), 0.9, 30, 0.35);
    this.audio.rockHit(at.distanceTo(this.ship.position));
    if (rock.hp <= 0) this.destroyRock(rock, local);
  }

  destroyRock(rock: Rock, local: boolean): void {
    if (this.asteroids.isDestroyed(rock.id, this.now())) return;
    this.asteroids.markDestroyed(rock.id, this.now());
    this.effects.shatter(this.asteroids.frameOf(rock), rock.world, rock.radius, rock.variant);
    const dist = rock.world.distanceTo(this.ship.position);
    this.audio.shatter(dist);
    this.addTrauma(Math.min(0.5, (rock.radius / Math.max(dist, 1)) * 2));
    if (local) {
      const pts = ASTEROIDS.pointsPerRock * Math.max(1, Math.round(rock.radius / 20));
      this.score += pts;
      this.logbook.setPoints(this.score);
      this.lastEvent = `+${pts} · asteroid destroyed`;
      this.onRockDestroyed(rock.id);
    }
  }

  private updateShake(dt: number): void {
    this.trauma = Math.max(0, this.trauma - COMBAT.traumaDecay * dt);
    this.shakeT += dt * 32;
    const k = this.trauma * this.trauma * this.ship.spec.length * 0.06;
    this.shipCamera.shake.set(Math.sin(this.shakeT * 1.7) * k, Math.sin(this.shakeT * 2.3 + 1) * k, Math.sin(this.shakeT * 1.3 + 2) * k * 0.5);
    this.damageFlash = Math.max(0, this.damageFlash - dt * 1.5);
  }

  private update(dt: number): void {
    this.frame++;
    this.stack.resize(this.camera);
    this.universe.update(this.now());
    // Drag the ship with its frame first so rock positions and the ship share this frame's ephemeris.
    this.asteroids.refresh(dt, this.now(), this.ship.position);

    for (const action of this.input.drainActions()) {
      if (action === 'camera') this.shipCamera.toggle();
      if (action === 'target') this.cycleTarget();
      if (action === 'autopilot') this.toggleAutopilot();
      if (action === 'map' && this.mode === 'play') this.map.toggle();
      // The same Esc that released pointer lock may also arrive as a key press: don't undo that pause.
      if (action === 'pause' && this.mode === 'play' && performance.now() - this.pausedByUnlockAt > 400) this.setPaused(!this.pause.open);
      if (action === 'mute') this.pause.setMuted(this.audio.toggleMute());
      if (action === 'chat' && this.mode === 'play' && this.mp && !this.mp.social.chatOpen) this.mp.social.openChat();
      if (action === 'players' && this.mode === 'play') this.mp?.social.toggleList();
      if (action === 'follow' && this.mode === 'play') this.follow();
      if (action === 'warp' && this.mode === 'play') {
        const evts: ShipEvent[] = [];
        const refused = this.ship.toggleWarp(evts);
        if (refused) {
          this.lastEvent = refused;
          this.audio.ui('click');
        }
        this.handleShipEvents(evts);
      }
    }
    const chatting = !!this.mp?.social.chatOpen;
    this.input.enabled = this.mode === 'play' && !this.map.open && !this.pause.open && !chatting;
    this.input.read(dt, this.intent);
    const targetInfo = this.targetInfo();
    if (this.autopilot.engaged) {
      // Manual stick input or throttle cancels the autopilot.
      if (Math.abs(this.intent.pitch) + Math.abs(this.intent.yaw) + Math.abs(this.intent.roll) > 0.5 || this.intent.throttleDelta !== 0 || this.intent.fullStop) {
        this.autopilot.disengage(this.ship);
        this.lastEvent = 'Autopilot disengaged';
      } else if (targetInfo) {
        this.autopilot.update(dt, this.ship, targetInfo, this.universe);
        if (this.autopilot.arrived) this.lastEvent = `Arrived at ${targetInfo.name}`;
        // Long hops: let the autopilot use the warp drive.
        const evts: ShipEvent[] = [];
        const rem = this.autopilot.remaining;
        if (this.autopilot.engaged && rem > WARP.autopilotEngageAbove && this.ship.warp === 'off' && !this.ship.warpBlocked()) this.ship.toggleWarp(evts);
        else if (this.ship.warp !== 'off' && (rem < WARP.autopilotDisengageBelow || !this.autopilot.engaged)) this.ship.dropWarp(evts, `Warp dropout · approaching ${targetInfo.name}`);
        this.handleShipEvents(evts);
      }
      this.intent.pitch = this.intent.yaw = this.intent.roll = 0;
    }
    this.events.length = 0;
    if (this.ship.dead) {
      this.respawnTimer = (this.respawnTimer ?? 0) - dt;
      if (this.ship.warp !== 'off') this.ship.dropWarp([], 'destroyed');
      if (this.respawnTimer <= 0) {
        this.respawnTimer = null;
        this.spawn();
        this.lastEvent = 'Respawned';
        this.onRespawn();
      }
    } else if (this.mode === 'menu') {
      // Parked at the spawn point, dragged along with Earth.
      this.ship.position.add(this.universe.get('earth').delta);
      this.ship.throttle = 0.25;
    } else {
      this.ship.proximity = Math.min(this.asteroids.nearestSurface(this.ship.position), this.mp?.nearestShip(this.ship.position) ?? Infinity);
      this.ship.update(dt, this.intent, this.universe, this.heightFn, this.events);
    }
    this.handleShipEvents(this.events);
    if (this.mode === 'play') this.updateCombat(dt);
    this.updateShake(dt);
    this.updateWarpFx(dt);
    this.logCheckTimer -= dt;
    if (this.mode === 'play' && !this.ship.dead && this.logCheckTimer <= 0) {
      this.logCheckTimer = 0.5;
      this.logbook.check(this.universe, this.ship.position, this.asteroids.inBelt(this.ship.position));
    }
    this.afterSimulation(dt);
  }

  private handleShipEvents(events: ShipEvent[]): void {
    for (const e of events) {
      if (e.type === 'warp') {
        if (e.state === 'spool') {
          this.lastEvent = 'Warp drive spooling…';
          this.audio.warpSpool();
        } else if (e.state === 'on') {
          this.lastEvent = 'Warp';
          this.audio.warpJump();
          this.addTrauma(0.55);
          this.effects.flash(this.ship.frameBody ?? this.universe.get('sun'), this.ship.position.clone().addScaledVector(this.ship.forward(), this.ship.spec.length * 2), this.ship.spec.length * 3, 0.5, new THREE.Color(0.6, 0.85, 1));
        } else {
          this.lastEvent = e.reason ?? 'Warp disengaged';
          this.audio.warpDrop();
          this.addTrauma(0.35);
        }
      }
      if (e.type === 'landed') {
        this.lastEvent = `Landed on ${this.universe.get(e.bodyId).def.name}`;
        this.audio.landing();
        if (e.bodyId === 'earth') this.logbook.mark('land:earth');
      }
      if (e.type === 'crash') this.kill(`Crashed into ${this.universe.get(e.bodyId).def.name} at ${e.speed.toFixed(0)} m/s`);
      if (e.type === 'takeoff') this.lastEvent = 'Lift-off';
    }
  }

  private updateWarpFx(dt: number): void {
    const target = this.ship.warp === 'on' ? 1 : this.ship.warp === 'spool' ? 0.3 * this.ship.warpCharge : 0;
    this.warpTunnel.update(dt, this.mode === 'play' ? target : 0);
    this.shipCamera.warpLevel = this.warpTunnel.level;
    this.audio.warpLevel(this.warpTunnel.level, this.ship.warp === 'spool');
    if (this.ship.warp === 'on') this.trauma = Math.max(this.trauma, 0.12);
  }

  /** Camera, world placement, UI: runs after the simulation step. */
  private afterSimulation(dt: number): void {
    const targetInfo = this.targetInfo();

    if (this.mode === 'menu') this.updateMenuCamera(dt);
    else this.shipCamera.update(dt, this.ship);
    const origin = this.shipCamera.worldPosition;
    this.view.update(origin, this.now() / 1000, this.camera, new THREE.Vector3());
    this.updateEarthTiles(origin);
    // Eye adaptation: open up under a daylit sky.
    // ...and stop down when the sun's disc is large (close approach).
    const sunAngle = this.universe.get('sun').def.radius / Math.max(1, this.universe.get('sun').position.distanceTo(origin));
    const sunStop = 1 / (1 + Math.max(0, sunAngle - 0.01) * 30);
    const targetExposure = RENDER.exposure * (1 + 1.6 * this.view.daylight) * sunStop;
    const r = this.stack.renderer;
    r.toneMappingExposure += (targetExposure - r.toneMappingExposure) * (1 - Math.exp(-2 * dt));

    this.shipVisual.group.position.subVectors(this.ship.position, origin);
    this.shipVisual.group.quaternion.copy(this.ship.quaternion);
    this.shipVisual.setThrust(this.ship.throttle, this.ship.boosting, dt);
    this.audio.engine(this.ship.throttle, this.ship.boosting, this.mode === 'play' && !this.ship.dead);
    const cockpitView = this.shipCamera.mode === 'cockpit' && this.mode === 'play' && !this.ship.dead;
    this.shipVisual.setModelVisible(!this.ship.dead);
    this.shipVisual.setCockpitMode(cockpitView);
    this.cockpit.setVisible(cockpitView);
    this.hud.setCockpit(cockpitView);
    if (cockpitView) this.cockpit.update(dt, this.cockpitReadout(targetInfo));
    this.asteroids.draw(origin);
    this.belt.update(this.now(), origin);
    this.effects.update(dt, origin);

    // Dust streaks use velocity relative to the dragged local frame.
    this.localVelocity.copy(this.ship.velocity);
    this.dust.update(dt, this.localVelocity, this.view.daylight);
    this.dust.object.quaternion.identity();

    this.mp?.update(dt, origin);
    this.markers.update(this.markerItems(), origin, this.camera, this.targetKey(), this.ship.position, this.occluders);
    this.map.draw(this.mapPoints(), this.targetKey());

    this.hud.update({
      speed: this.ship.speed,
      maxSpeed: this.ship.maxSpeed,
      throttle: this.ship.throttle,
      altitude: this.ship.altitude,
      nearest: this.ship.nearestBody?.def.name ?? '',
      landed: !!this.ship.landed,
      status: this.lastEvent,
      pointerLocked: this.input.pointerLocked,
      stick: this.input.stick,
      target: targetInfo
        ? {
            name: targetInfo.name,
            distance: Math.max(0, targetInfo.position.distanceTo(this.ship.position) - targetInfo.radius),
            eta: this.eta(targetInfo),
            autopilot: this.autopilot.engaged,
            color: targetInfo.color,
          }
        : null,
      shield: this.ship.shield,
      maxShield: this.ship.maxShield,
      score: this.score,
      respawnIn: this.respawnTimer,
      damage: this.damageFlash,
      logbook: { count: this.logbook.count, total: this.logbook.total, next: this.logbook.next()?.label ?? 'Grand Tour complete' },
      warp: { state: this.ship.warp, charge: this.ship.warpCharge, blocked: this.ship.warp === 'off' ? this.ship.warpBlocked() : null },
    });
  }

  private cockpitReadout(targetInfo: TargetInfo | null): CockpitReadout {
    const ship = this.ship;
    const inv = ship.quaternion.clone().invert();
    const radar: CockpitReadout['radar'] = [];
    const push = (p: THREE.Vector3, kind: 'rock' | 'player' | 'target') => {
      const l = p.clone().sub(ship.position).applyQuaternion(inv);
      if (l.length() < 60_000) radar.push({ x: l.x, y: l.y, z: l.z, kind });
    };
    for (const r of this.asteroids.active) if (radar.length < 120) push(r.world, 'rock');
    for (const m of this.mp?.markers() ?? []) push(m.position, 'player');
    if (targetInfo) push(targetInfo.position, 'target');
    const blocked = ship.warp === 'off' ? ship.warpBlocked() : null;
    return {
      speed: formatSpeed(ship.speed),
      altitude: ship.landed ? 'LANDED' : formatDistance(ship.altitude),
      nearest: ship.nearestBody?.def.name ?? '',
      target: targetInfo?.name ?? '',
      targetDistance: targetInfo ? formatDistance(Math.max(0, targetInfo.position.distanceTo(ship.position) - targetInfo.radius)) : '',
      throttle: ship.throttle,
      shield: ship.shield / ship.maxShield,
      warp: ship.warp === 'off' && blocked ? 'locked' : ship.warp,
      warpCharge: ship.warpCharge,
      boosting: ship.boosting,
      radar,
      timeSeconds: performance.now() / 1000,
    };
  }

  // ---------------- Earth terrain ----------------
  private readonly tmpBF = new THREE.Vector3();
  private readonly invRot = new THREE.Matrix4();

  private readonly heightFn = (body: import('../astro/Universe').BodyState, worldPos: THREE.Vector3): number => {
    if (body.def.id !== 'earth' || !this.earthTiles.active) return 0;
    this.invRot.copy(body.rotation).invert();
    const dir = this.tmpBF.subVectors(worldPos, body.position).applyMatrix4(this.invRot).normalize();
    return this.earthTiles.heightAt(dir);
  };

  private updateEarthTiles(origin: THREE.Vector3): void {
    const earth = this.universe.get('earth');
    this.invRot.copy(earth.rotation).invert();
    const camBF = new THREE.Vector3().subVectors(origin, earth.position).applyMatrix4(this.invRot);
    const originToEarth = new THREE.Vector3().subVectors(earth.position, origin);
    this.earthTiles.setSharedTextures(this.view.textures.get('earth_night'), this.view.textures.get('earth_clouds'));
    this.earthTiles.update(camBF, originToEarth, earth.quaternion, this.view.sunRender, this.view.cloudShiftFor('earth', this.now() / 1000), this.view.daylight);
    this.view.setSurfaceSink('earth', this.earthTiles.active ? 400 : 0);
  }

  // ---------------- Targets ----------------
  targetKey(): string | null {
    return this.target ? `${this.target.kind}:${this.target.id}` : null;
  }

  setTarget(t: TargetRef | null): void {
    this.target = t;
    if (this.autopilot.engaged) this.autopilot.disengage(this.ship);
    const info = this.targetInfo();
    if (info) this.lastEvent = `Target: ${info.name}`;
  }

  /** Cycles through bodies (and players, M6), nearest first after the current one. */
  cycleTarget(): void {
    const list = this.targetCandidates();
    const i = list.findIndex((t) => `${t.kind}:${t.id}` === this.targetKey());
    this.setTarget(list[(i + 1) % list.length]);
  }

  protected targetCandidates(): TargetRef[] {
    const players: TargetRef[] = this.mp?.targets() ?? [];
    return [...players, ...this.universe.bodies.map((b) => ({ kind: 'body' as const, id: b.def.id }))];
  }

  /** G: autopilot onto the targeted player and keep station 150 m behind them. */
  follow(): void {
    if (this.target?.kind !== 'player') {
      const first = this.mp?.targets()[0];
      if (!first) {
        this.lastEvent = 'Nobody to follow yet';
        return;
      }
      this.setTarget(first);
    }
    if (!this.autopilot.engaged) this.toggleAutopilot();
    this.lastEvent = `Following ${this.targetInfo()?.name ?? ''}`;
  }

  targetInfo(): TargetInfo | null {
    const t = this.target;
    if (!t) return null;
    if (t.kind === 'body') {
      const b = this.universe.get(t.id);
      return { ref: t, name: b.def.name, position: b.position, radius: b.def.radius, color: b.def.mapColor };
    }
    return this.playerTargetInfo(t.id);
  }

  protected playerTargetInfo(id: string): TargetInfo | null {
    return this.mp?.targetInfo(id) ?? null;
  }

  toggleAutopilot(): void {
    if (this.autopilot.engaged) {
      this.autopilot.disengage(this.ship);
      this.lastEvent = 'Autopilot disengaged';
      return;
    }
    if (!this.targetInfo()) {
      this.lastEvent = 'No target — press T';
      return;
    }
    if (this.ship.landed) this.ship.throttle = 0.1;
    this.autopilot.engage();
    this.lastEvent = 'Autopilot engaged';
  }

  private eta(t: TargetInfo): number {
    const dist = Math.max(0, t.position.distanceTo(this.ship.position) - this.autopilot.stopDistance(t));
    const toward = t.position.clone().sub(this.ship.position).normalize().dot(this.ship.velocity);
    if (toward <= 1) return Infinity;
    // Speed scales with altitude, so a constant-speed estimate is pessimistic near bodies; cap at cruise.
    return dist / toward;
  }

  protected markerItems(): MarkerInput[] {
    const out: MarkerInput[] = [];
    for (const b of this.universe.bodies) {
      const d = b.def;
      let important = d.kind !== 'moon';
      if (d.kind === 'moon' && d.parent) {
        const parent = this.universe.get(d.parent);
        important = parent.position.distanceTo(this.ship.position) < parent.def.soi * 0.5;
      }
      out.push({ key: `body:${d.id}`, label: d.name, position: b.position, radius: d.radius, color: d.mapColor, kind: d.kind === 'moon' ? 'moon' : 'body', important });
    }
    if (this.mp) out.push(...this.mp.markers());
    return out;
  }

  protected mapPoints(): MapPoint[] {
    return [{ key: 'self', label: this.options.name, position: this.ship.position, color: '#7fe7ff', kind: 'self' }, ...(this.mp?.mapPoints() ?? [])];
  }

  private render(): void {
    this.stack.renderer.info.reset();
    this.stack.composer.render();
  }

  private installTestHooks(): void {
    window.__TEST__ = {
      state: () => ({
        frame: this.frame,
        speed: this.ship.speed,
        throttle: this.ship.throttle,
        altitude: this.ship.altitude,
        nearest: this.ship.nearestBody?.def.id,
        landed: !!this.ship.landed,
        camera: this.shipCamera.mode,
        texturesLoading: this.view.textures.loading,
        calls: this.stack.renderer.info.render.calls,
        tiles: this.earthTiles.visibleTiles,
        shield: this.ship.shield,
        dead: this.ship.dead,
        score: this.score,
        rocksActive: this.asteroids.active.length,
        mode: this.mode,
        warp: this.ship.warp,
        warpBlocked: this.ship.warpBlocked(),
        proximity: Math.round(this.ship.proximity),
        maxSpeed: this.ship.maxSpeed,
        paused: this.pause.open,
        logbook: this.logbook.count,
        ship: this.ship.spec.id,
        netId: this.mp?.net.id ?? null,
        remotes: this.mp ? [...this.mp.remotes.players.values()].map((r) => ({ name: r.info.name, dist: Math.round(r.position.distanceTo(this.ship.position)), hasState: !!r.state })) : [],
        shotsFired: this.shotsFired,
        daylight: this.view.daylight,
        exposure: this.stack.renderer.toneMappingExposure,
        sunElevationDeg: (() => {
          const b = this.ship.nearestBody ?? this.universe.get('earth');
          const up = this.ship.position.clone().sub(b.position).normalize();
          const sd = this.universe.get('sun').position.clone().sub(this.ship.position).normalize();
          return (Math.asin(up.dot(sd)) * 180) / Math.PI;
        })(),
        tilesPending: this.earthTiles.pending,
        triangles: this.stack.renderer.info.render.triangles,
      }),
      /** teleport near a body: lat, lon, altitude (m) */
      teleport: (bodyId: string, lat: number, lon: number, alt: number) => {
        this.ship.placeAt(this.universe, this.universe.get(bodyId), lat, lon, alt);
        this.shipCamera.snap(this.ship);
      },
      /** put the ship `agl` metres above the currently loaded terrain, keeping lat/lon */
      setAGL: (agl: number) => {
        const body = this.ship.nearestBody ?? this.universe.get('earth');
        const rel = this.ship.position.clone().sub(body.position);
        const h = this.heightFn(body, this.ship.position);
        this.ship.position.copy(body.position).add(rel.setLength(body.def.radius + h + agl));
        this.ship.landed = null;
        this.shipCamera.snap(this.ship);
        return h;
      },
      setThrottle: (t: number) => {
        this.ship.throttle = t;
      },
      lookAt: (bodyId: string) => {
        const b = this.universe.get(bodyId);
        const dir = b.position.clone().sub(this.ship.position).normalize();
        const m = new THREE.Matrix4().lookAt(new THREE.Vector3(), dir, this.ship.up());
        this.ship.quaternion.setFromRotationMatrix(m);
        this.shipCamera.snap(this.ship);
      },
      rotateShip: (pitchDeg: number, yawDeg: number) => {
        this.ship.quaternion.multiply(
          new THREE.Quaternion().setFromEuler(new THREE.Euler((pitchDeg * Math.PI) / 180, (yawDeg * Math.PI) / 180, 0)),
        );
        this.shipCamera.snap(this.ship);
      },
      toggleCamera: () => this.shipCamera.toggle(),
      setTarget: (kind: 'body' | 'player', id: string) => this.setTarget({ kind, id }),
      autopilot: () => this.toggleAutopilot(),
      autopilotState: () => ({ engaged: this.autopilot.engaged, arrived: this.autopilot.arrived, remaining: this.autopilot.remaining }),
      toggleMap: () => this.map.toggle(),
      /** aim the ship at the nearest active rock */
      /** park `dist` metres from the nearest rock and aim at it */
      approachNearestRock: (dist: number) => {
        let best: Rock | null = null;
        for (const r of this.asteroids.active) if (!best || r.world.distanceTo(this.ship.position) < best.world.distanceTo(this.ship.position)) best = r;
        if (!best) return null;
        const dir = best.world.clone().sub(this.ship.position).normalize();
        this.ship.position.copy(best.world).addScaledVector(dir, -(dist + best.radius));
        this.ship.velocity.set(0, 0, 0);
        this.ship.quaternion.setFromRotationMatrix(new THREE.Matrix4().lookAt(new THREE.Vector3(), dir, this.ship.up()));
        this.shipCamera.snap(this.ship);
        return { id: best.id, radius: best.radius, hp: best.hp };
      },
      aimNearestRock: () => {
        let best: Rock | null = null;
        for (const r of this.asteroids.active) if (!best || r.world.distanceTo(this.ship.position) < best.world.distanceTo(this.ship.position)) best = r;
        if (!best) return null;
        const dir = best.world.clone().sub(this.ship.position).normalize();
        this.ship.quaternion.setFromRotationMatrix(new THREE.Matrix4().lookAt(new THREE.Vector3(), dir, this.ship.up()));
        this.shipCamera.snap(this.ship);
        return { id: best.id, distance: best.world.distanceTo(this.ship.position), radius: best.radius };
      },
      spawn: () => this.spawn(),
      logbookMark: (id: string) => this.logbook.mark(id),
      menuView: (angleDeg: number, distance = 1.15) => {
        this.menuAngle = (angleDeg * Math.PI) / 180;
        this.menuDistance = distance;
        this.menuFrozen = true;
      },
      isRockDestroyed: (id: string) => this.asteroids.isDestroyed(id, this.now()),
      /** test: place the ship `dist` m from a remote player, looking at them */
      parkNear: (id: string, dist: number) => {
        const r = this.mp?.remotes.players.get(id);
        if (!r) return false;
        this.autopilot.disengage(this.ship);
        const back = new THREE.Vector3(0.4, 0.5, 1).normalize().applyQuaternion(r.quaternion);
        const pos = r.position.clone().addScaledVector(back, dist);
        const up = new THREE.Vector3(0, 1, 0).applyQuaternion(r.quaternion);
        this.ship.placeWorld(pos, up, r.position.clone().sub(pos).normalize());
        this.shipCamera.snap(this.ship);
        return true;
      },
      damage: (v: number) => this.damage(v),
      teleportBelt: () => {
        const earth = this.universe.get('earth');
        const dir = earth.position.clone().setZ(0).normalize();
        this.ship.placeWorld(dir.multiplyScalar(2.7 * 1.495978707e11), new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 1, 0));
        this.shipCamera.snap(this.ship);
      },
      /** place above Earth where the sun stands at `sunElevationDeg`, looking along the horizon */
      teleportDaylight: (alt: number, sunElevationDeg: number) => {
        const earth = this.universe.get('earth');
        const sunDir = this.universe.get('sun').position.clone().sub(earth.position).normalize();
        const local = sunDir.applyMatrix4(new THREE.Matrix4().extractRotation(earth.rotation).invert());
        const lat = (Math.asin(local.z) * 180) / Math.PI;
        const lon = (Math.atan2(local.y, local.x) * 180) / Math.PI - (90 - sunElevationDeg);
        this.ship.placeAt(this.universe, earth, lat, lon, alt);
        this.shipCamera.snap(this.ship);
        return { lat, lon };
      },
    };
  }

  dispose(): void {
    this.loop.stop();
    this.input.dispose();
    this.stack.renderer.dispose();
  }
}
